"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PlatformSignupModal } from "./PlatformSignupModal";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { useI18n } from "@/i18n/I18nProvider";

import styles from "./HubeeHomePage.module.css";

export default function HubeeHomePage() {
  const router = useRouter();
  const { user, isLoading: userLoading } = useCurrentUser();
  const { messages: m } = useI18n();

  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  const userId =
    Number((user as any)?.userId) ||
    Number((user as any)?.customer_id) ||
    0;

  const isLoggedIn = !!userId;

  async function handleStart() {
    if (userLoading) return;

    const localUserId =
      Number((user as any)?.userId) ||
      Number((user as any)?.customer_id) ||
      0;

    if (localUserId) {
      router.push("/setup");
      return;
    }

    try {
      const res = await fetch("/api/auth/me", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => null);

      const serverUserId =
        Number(data?.user?.userId) ||
        Number(data?.user?.customer_id) ||
        Number(data?.userId) ||
        Number(data?.customer_id) ||
        0;

      if (res.ok && serverUserId) {
        router.push("/setup");
        return;
      }
    } catch {}

    setIsSignupOpen(true);
  }

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;

    const revealNodes = Array.from(
      root.querySelectorAll<HTMLElement>("[data-reveal]")
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.visible = "true";
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.13 }
    );

    revealNodes.forEach((node) => observer.observe(node));

    const hero = root.querySelector<HTMLElement>("[data-hero]");
    const avatar = root.querySelector<HTMLElement>("[data-avatar]");
    const floaters = Array.from(
      root.querySelectorAll<HTMLElement>("[data-hero-float]")
    );

    if (!hero || !avatar || !window.matchMedia("(pointer:fine)").matches) {
      return () => observer.disconnect();
    }

    const onMove = (event: MouseEvent) => {
      const rect = hero.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      avatar.style.transform = `translate3d(${x * 12}px, ${y * 8}px, 0)`;

      floaters.forEach((element, index) => {
        const force = (index + 1) * 1.8;
        element.style.transform = `translate3d(${x * force}px, ${y * force}px, 0)`;
      });
    };

    const onLeave = () => {
      avatar.style.transform = "";
      floaters.forEach((element) => {
        element.style.transform = "";
      });
    };

    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);

    return () => {
      observer.disconnect();
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const startLabel = userLoading
    ? m.common.loading
    : isLoggedIn
      ? m.common.continue
      : m.common.startNow;

  return (
    <div ref={pageRef} className={styles.homePage}>
      <header className={styles.header}>
        <div className={`${styles.container} ${styles.nav}`}>
          <a className={styles.logo} href="#top" aria-label="Hubee">
            h<span>u</span>bee
          </a>

          <nav className={styles.navLinks} aria-label={m.header.aria}>
            <a href="#how">{m.header.how}</a>
            <a href="#experience">{m.header.experience}</a>
            <a href="#samantha">{m.header.samantha}</a>
            <a href="#impact">{m.header.impact}</a>
            <a href="#whitelabel">{m.header.whitelabel}</a>
          </nav>

          <div className={styles.navActions}>
            <LanguageSwitcher />
            <Link className={styles.navTextLink} href="/pricing">
              {m.common.pricing}
            </Link>
            <Link className={styles.navTextLink} href="/login">
              {m.common.login}
            </Link>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleStart}
              disabled={userLoading}
            >
              {startLabel}
            </button>
          </div>
        </div>
      </header>

      <main id="top">
        <section className={styles.hero} data-hero>
          <div className={styles.heroGlow} />
          <div className={`${styles.container} ${styles.heroGrid}`}>
            <div className={styles.heroCopy} data-reveal data-visible="true">
              <span className={styles.eyebrow}>{m.hero.eyebrow}</span>

              <h1>
                {m.hero.titleBefore}{" "}
                <span className={styles.yellow}>{m.hero.titleAccent}</span>
              </h1>

              <p className={styles.lead}>
                {m.hero.leadStrong} {m.hero.lead}
              </p>

              <div className={styles.heroActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLarge}`}
                  onClick={handleStart}
                  disabled={userLoading}
                >
                  {startLabel}
                </button>

                <Link
                  className={`${styles.btn} ${styles.btnLarge}`}
                  href="/pricing"
                >
                  {m.common.viewPricing} <span>→</span>
                </Link>
              </div>

              <div className={styles.heroNote}>
                <span className={styles.noteDot}>✦</span>
                <span>{m.hero.note}</span>
              </div>
            </div>

            <div
              className={styles.heroVisual}
              data-reveal
              data-visible="true"
              aria-label={m.hero.visualAria}
            >
              <div className={styles.avatarWrap} data-avatar>
                <img
                  src="/images/users/21503/1786575094493_ce2b3b1397ba.png"
                  alt={m.hero.avatarAlt}
                />
              </div>

              <HeroFloat
                className={styles.floatMission}
                icon="✦"
                title={m.hero.floats.mission.title}
                copy={m.hero.floats.mission.copy}
                progress={28}
              />
              <HeroFloat
                className={styles.floatCourse}
                icon="▣"
                title={m.hero.floats.course.title}
                copy={m.hero.floats.course.copy}
                progress={42}
              />
              <HeroFloat
                className={styles.floatEvent}
                icon="◉"
                title={m.hero.floats.event.title}
                copy={m.hero.floats.event.copy}
              />
              <HeroFloat
                className={styles.floatAway}
                icon="↻"
                title={m.hero.floats.away.title}
                copy={m.hero.floats.away.copy}
              />

              <div
                className={`${styles.floatingCard} ${styles.floatSamantha}`}
                data-hero-float
              >
                <div className={styles.floatRow}>
                  <div className={styles.samWave} aria-hidden="true">
                    <i /><i /><i /><i /><i />
                  </div>
                  <div>
                    <div className={styles.miniTitle}>Samantha</div>
                    <div className={styles.miniCopy}>
                      {m.hero.floats.samantha}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className={styles.signalStrip} aria-hidden="true">
          <div className={styles.signalTrack}>
            {[...m.signal, ...m.signal].map((item, index) => (
              <span key={`${item.strong}-${index}`}>
                <strong>{item.strong}</strong> {item.copy}
              </span>
            ))}
          </div>
        </div>

        <section id="how" className={`${styles.block} ${styles.problem}`}>
          <div className={styles.patternDots} />
          <div className={styles.container}>
            <SectionHeading
              centered
              eyebrow={m.problem.eyebrow}
              title={
                <>
                  {m.problem.titleBefore}{" "}
                  <span className={styles.yellow}>{m.problem.titleAccent}</span>{" "}
                  {m.problem.titleAfter}
                </>
              }
              lead={m.problem.lead}
            />

            <div className={styles.problemSplit} data-reveal>
              <ProblemSide
                label={m.problem.before.label}
                title={m.problem.before.title}
                items={m.problem.before.items}
                flow={m.problem.before.flow}
              />
              <ProblemSide
                good
                label={m.problem.after.label}
                title={m.problem.after.title}
                items={m.problem.after.items}
                flow={m.problem.after.flow}
              />
            </div>
          </div>
        </section>

        <section id="experience" className={`${styles.block} ${styles.missions}`}>
          <div className={`${styles.container} ${styles.featureSplit}`}>
            <div data-reveal>
              <span className={styles.eyebrow}>{m.activation.eyebrow}</span>
              <h2>
                {m.activation.titleBefore}{" "}
                <span className={styles.yellow}>{m.activation.titleAccent}</span>
              </h2>
              <p className={styles.lead}>{m.activation.lead}</p>
              <p className={styles.mutedCopy}>{m.activation.copy}</p>
            </div>

            <div className={styles.missionStage} data-reveal>
              <div className={styles.missionCard}>
                <div className={styles.missionKicker}>{m.activation.card.kicker}</div>
                <h3>{m.activation.card.title}</h3>
                <p>{m.activation.card.copy}</p>
                <button
                  type="button"
                  onClick={handleStart}
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLarge} ${styles.fullButton}`}
                >
                  {m.activation.card.cta}
                </button>
              </div>

              <div className={styles.xpPop}>
                <strong>{m.activation.xp.value}</strong>
                <small>{m.activation.xp.copy}</small>
              </div>

              <div className={styles.path}>
                <span className={`${styles.node} ${styles.nodeActive}`} />
                <span className={styles.node} />
                <span className={styles.node} />
                <span className={styles.node} />
                <span className={`${styles.node} ${styles.nodeDiamond}`} />
              </div>
            </div>
          </div>
        </section>

        <section className={`${styles.block} ${styles.smart}`}>
          <div className={styles.container}>
            <SectionHeading
              eyebrow={m.direction.eyebrow}
              title={
                <>
                  {m.direction.titleBefore}{" "}
                  <span className={styles.smartAccent}>{m.direction.titleAccent}</span>
                </>
              }
              lead={m.direction.lead}
            />
            <div className={styles.smartShowcase} data-reveal>
              <img
                className={styles.smartVisualImage}
                src="/images/users/10086/1786748418015_e691f93f6f50.png"
                alt={m.direction.imageAlt}
              />
            </div>
          </div>
        </section>

        <section className={`${styles.block} ${styles.knowledge}`}>
          <div className={`${styles.container} ${styles.knowledgeStage}`}>
            <div className={styles.knowledgeVisual} data-reveal>
              <img
                src="/images/users/10086/1786750921530_df3e1390e2a6.png"
                alt={m.knowledge.imageAlt}
                className={styles.knowledgeVisualImage}
              />
            </div>

            <div data-reveal>
              <span className={styles.eyebrow}>{m.knowledge.eyebrow}</span>
              <h2>
                {m.knowledge.titleBefore}{" "}
                <span className={styles.yellow}>{m.knowledge.titleAccent}</span>
              </h2>
              <p className={styles.lead}>{m.knowledge.lead}</p>
              <div className={styles.knowledgeSummary}>
                <div className={styles.knowledgeBig}>{m.knowledge.summary}</div>
                <small>{m.knowledge.small}</small>
              </div>
            </div>
          </div>
        </section>

        <section id="samantha" className={`${styles.block} ${styles.samanthaSection}`}>
          <div className={`${styles.container} ${styles.samanthaGrid}`}>
            <div data-reveal>
              <span className={styles.eyebrow}>{m.samantha.eyebrow}</span>
              <h2>
                {m.samantha.titleBefore}
                <br />
                {m.samantha.titleMiddle}{" "}
                <span className={styles.yellow}>{m.samantha.titleAccent}</span>
              </h2>
              <p className={styles.lead}>{m.samantha.lead}</p>

              <div className={styles.samPoints}>
                {m.samantha.points.map((point, index) => (
                  <div className={styles.samPoint} key={point.title}>
                    <div className={styles.samPointNumber}>
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <strong>{point.title}</strong>
                      <span>{point.copy}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.samOrb} data-reveal aria-hidden="true">
              <div className={styles.waveBig}>
                <i /><i /><i /><i /><i /><i /><i />
              </div>
            </div>
          </div>
        </section>

        <section id="impact" className={`${styles.block} ${styles.impact}`}>
          <div className={`${styles.container} ${styles.impactStage}`}>
            <div data-reveal>
              <span className={styles.eyebrow}>{m.impact.eyebrow}</span>
              <h2>
                {m.impact.titleBefore}{" "}
                <span className={styles.impactAccent}>{m.impact.titleAccent}</span>
              </h2>
              <p className={styles.lead}>{m.impact.lead}</p>
              <p className={styles.impactMuted}>{m.impact.copy}</p>
            </div>

            <div data-reveal>
              <div className={styles.ranking}>
                <div className={styles.rankHead}>
                  <div>
                    <small>{m.impact.ranking.eyebrow}</small>
                    <h3>{m.impact.ranking.title}</h3>
                  </div>
                  <span>{m.impact.ranking.period}</span>
                </div>

                {m.impact.ranking.people.map((person, index) => (
                  <div className={styles.rankRow} key={person.name}>
                    <div className={index === 0 ? styles.firstRank : undefined}>
                      {index + 1}
                    </div>
                    <div>
                      <strong>{person.name}</strong>
                      <small>{person.copy}</small>
                    </div>
                    <div className={styles.rankValue}>{person.energy}</div>
                  </div>
                ))}
              </div>

              <div className={styles.impactCards}>
                <div className={styles.energyCard}>
                  <small>{m.impact.energy.label}</small>
                  <div className={styles.energyRing}><b>72%</b></div>
                  <strong>{m.impact.energy.copy}</strong>
                </div>

                <div className={styles.boostMini}>
                  <span className={styles.boostStatus}>● {m.impact.boost.status}</span>
                  <h3>{m.impact.boost.title}</h3>
                  <div className={styles.boostSteps}>
                    {m.impact.boost.steps.map((step) => <span key={step}>{step}</span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="whitelabel" className={`${styles.block} ${styles.whitelabel}`}>
          <div className={styles.container}>
            <SectionHeading
              centered
              eyebrow={m.whitelabel.eyebrow}
              title={
                <>
                  {m.whitelabel.titleBefore}
                  <br />
                  <span className={styles.yellow}>{m.whitelabel.titleAccent}</span>
                </>
              }
              lead={m.whitelabel.lead}
            />

            <div className={styles.worlds}>
              {m.whitelabel.worlds.map((world, index) => (
                <div
                  key={world.name}
                  className={`${styles.world} ${
                    index === 0 ? styles.alma : index === 1 ? styles.nexo : styles.impulsa
                  }`}
                  data-reveal
                >
                  <div className={styles.tinyNav}>
                    {world.nav.map((item) => <span key={item}>{item}</span>)}
                  </div>
                  <h3>{world.name}</h3>
                  <p>{world.copy}</p>
                  <div className={styles.phoneMockup}>
                    <div className={styles.phoneBar} />
                    <div className={styles.phoneBox} />
                    <div className={styles.phoneBox} />
                    <div className={styles.phoneBox} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.infrastructure}>
          <div className={styles.container} data-reveal>
            <h2>
              {m.infrastructure.titleBefore}
              <br />
              <span className={styles.yellow}>{m.infrastructure.titleAccent}</span>
            </h2>
            <div className={styles.infrastructureRow}>
              {m.infrastructure.items.map((item) => (
                <span className={styles.infrastructurePill} key={item}>
                  <i />{item}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section id="cta" className={styles.final}>
          <div className={`${styles.container} ${styles.finalGrid}`}>
            <div data-reveal>
              <span className={styles.eyebrow}>{m.final.eyebrow}</span>
              <h2>
                {m.final.titleBefore}
                <br />
                <span className={styles.yellow}>{m.final.titleAccent}</span>
              </h2>
              <p className={styles.lead}>{m.final.lead}</p>
              <div className={styles.heroActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLarge}`}
                  onClick={handleStart}
                  disabled={userLoading}
                >
                  {startLabel}
                </button>
                <Link className={`${styles.btn} ${styles.btnLarge}`} href="/pricing">
                  {m.common.viewPricing}
                </Link>
              </div>
            </div>

            <div className={styles.finalVisual} data-reveal>
              <img
                src="/images/users/10086/1786749605941_242dcd0ff19f.png"
                alt={m.final.imageAlt}
              />
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={`${styles.container} ${styles.footerRow}`}>
          <div>{m.footer.copyright}</div>
          <div>{m.footer.tagline}</div>
        </div>
      </footer>

      <PlatformSignupModal
        isOpen={isSignupOpen}
        onClose={() => setIsSignupOpen(false)}
        next="/setup"
      />
    </div>
  );
}

function HeroFloat({
  className,
  icon,
  title,
  copy,
  progress,
}: {
  className: string;
  icon: string;
  title: string;
  copy: string;
  progress?: number;
}) {
  return (
    <div className={`${styles.floatingCard} ${className}`} data-hero-float>
      <div className={styles.floatRow}>
        <div className={styles.floatIcon}>{icon}</div>
        <div className={styles.floatContent}>
          <div className={styles.miniTitle}>{title}</div>
          <div className={styles.miniCopy}>{copy}</div>
        </div>
      </div>
      {typeof progress === "number" ? (
        <div className={styles.progress}><i style={{ width: `${progress}%` }} /></div>
      ) : null}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  lead,
  centered = false,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead: string;
  centered?: boolean;
}) {
  return (
    <div className={`${styles.sectionHead} ${centered ? styles.centered : ""}`} data-reveal>
      <span className={styles.eyebrow}>{eyebrow}</span>
      <h2>{title}</h2>
      <p className={styles.lead}>{lead}</p>
    </div>
  );
}

function ProblemSide({
  label,
  title,
  items,
  flow,
  good = false,
}: {
  label: string;
  title: string;
  items: string[];
  flow: string[];
  good?: boolean;
}) {
  return (
    <div className={`${styles.problemSide} ${good ? styles.problemGood : ""}`}>
      <div className={styles.problemLabel}>{label}</div>
      <h3>{title}</h3>
      <div className={styles.problemList}>
        {items.map((item) => (
          <div className={styles.problemItem} key={item}>
            <b>{good ? "✓" : "×"}</b>
            {item}
          </div>
        ))}
      </div>
      <div className={styles.flowMini}>
        {flow.map((item) => <span key={item}>{item}</span>)}
      </div>
    </div>
  );
}
